/**
 * MockProvider (SPEC 8.1): o `DataProvider` da UI que conversa com o worker da simulação.
 * Cada método vira uma chamada RPC serializável; `subscribe` vira uma assinatura no worker.
 * O worker roda fora do caminho crítico (carregado em paralelo ao shell; 10.4).
 */
import type {
  DataProvider,
  LiveMessage,
  PainelProviderV2,
  ProviderError,
  ScenarioId,
  SimControl,
  SimState,
  Topic,
  Unsubscribe,
} from '@rota/contracts';
import { whenPainted } from '@/lib/afterPaint';
import type { SimUrlOptions } from '../provider';
import {
  type CallMethod,
  type FromWorker,
  isScenario,
  isScenarioV2,
  type MockCoreOptions,
  type SimMethod,
  type ToWorker,
} from './protocol';

const HANDSHAKE_TIMEOUT_MS = 10_000;
/** A 2ª fase do boot do worker começa na 1ª pintura útil, ou depois deste tempo sem ela (TV). */
const WARM_FALLBACK_MS = 2500;

type Pending = { resolve(v: unknown): void; reject(e: ProviderError): void };

/** O que o MockProvider usa de um `Worker` (o real, ou um canal em memória nos testes). */
export interface WorkerPort {
  postMessage(message: ToWorker): void;
  addEventListener(type: 'message', listener: (e: MessageEvent<FromWorker>) => void): void;
  addEventListener(type: 'error', listener: (e: ErrorEvent) => void): void;
  removeEventListener(type: 'message', listener: (e: MessageEvent<FromWorker>) => void): void;
  removeEventListener(type: 'error', listener: (e: ErrorEvent) => void): void;
  terminate(): void;
}

export class MockProvider implements PainelProviderV2 {
  readonly kind = 'mock' as const;
  readonly sim: SimControl;

  private seq = 0;
  private subSeq = 0;
  private readonly pending = new Map<number, Pending>();
  private readonly subs = new Map<number, (m: LiveMessage) => void>();
  private readonly worker: WorkerPort;
  private warmed = false;

  constructor(worker: WorkerPort) {
    this.worker = worker;
    worker.addEventListener('message', (e: MessageEvent<FromWorker>) => this.onMessage(e.data));
    worker.addEventListener('error', (e) => this.failAll(e.message || 'Falha no worker da simulação'));
    this.sim = {
      get: () => this.rpc<SimState>('sim.get', []),
      set: (patch) => this.rpc<SimState>('sim.set', [patch]),
      resetDay: () => this.rpc<void>('sim.resetDay', []),
    };
  }

  private onMessage(m: FromWorker): void {
    if (m.type === 'result') {
      const p = this.pending.get(m.id);
      if (!p) return;
      this.pending.delete(m.id);
      if (m.ok) p.resolve(m.value);
      else p.reject(m.error);
    } else if (m.type === 'live') {
      this.subs.get(m.subId)?.(m.message);
    }
  }

  private failAll(message: string): void {
    for (const p of this.pending.values()) p.reject({ code: 'internal', message });
    this.pending.clear();
  }

  private post(m: ToWorker): void {
    this.worker.postMessage(m);
  }

  private rpc<T>(method: CallMethod | SimMethod, args: unknown[]): Promise<T> {
    const id = ++this.seq;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.post({ type: 'call', id, method, args });
    });
  }

  init: DataProvider['init'] = (viewerId) => this.rpc('init', [viewerId]);
  switchViewer: DataProvider['switchViewer'] = (role) => this.rpc('switchViewer', [role]);
  getSnapshot: DataProvider['getSnapshot'] = (q) => this.rpc('getSnapshot', [q]);
  getSeries: DataProvider['getSeries'] = (q) => this.rpc('getSeries', [q]);
  getFunnel: DataProvider['getFunnel'] = (q) => this.rpc('getFunnel', [q]);
  getInsights: DataProvider['getInsights'] = (q) => this.rpc('getInsights', [q]);
  getQuiz: DataProvider['getQuiz'] = (q) => this.rpc('getQuiz', [q]);
  getSegments: DataProvider['getSegments'] = (q) => this.rpc('getSegments', [q]);
  getRetention: DataProvider['getRetention'] = (q) => this.rpc('getRetention', [q]);
  getFinance: DataProvider['getFinance'] = (q) => this.rpc('getFinance', [q]);
  getTrackingHealth: DataProvider['getTrackingHealth'] = (q) => this.rpc('getTrackingHealth', [q]);
  getOrderJourney: DataProvider['getOrderJourney'] = (id) => this.rpc('getOrderJourney', [id]);
  getDecision: DataProvider['getDecision'] = (id) => this.rpc('getDecision', [id]);
  getKpiDetail: DataProvider['getKpiDetail'] = (q) => this.rpc('getKpiDetail', [q]);
  getChangesSince: DataProvider['getChangesSince'] = (since) => this.rpc('getChangesSince', [since]);
  list: DataProvider['list'] = (kind, q) => this.rpc('list', [kind, q]);
  status: DataProvider['status'] = () => this.rpc('status', []);
  decide: DataProvider['decide'] = (cmd) => this.rpc('decide', [cmd]);
  adCommand: DataProvider['adCommand'] = (cmd, opts) => this.rpc('adCommand', [cmd, opts]);
  recovery: DataProvider['recovery'] = (cmd) => this.rpc('recovery', [cmd]);
  revealPii: DataProvider['revealPii'] = (ref) => this.rpc('revealPii', [ref]);
  attributeManually: DataProvider['attributeManually'] = (orderId, target) =>
    this.rpc('attributeManually', [orderId, target]);
  updateSettings: DataProvider['updateSettings'] = (patch) => this.rpc('updateSettings', [patch]);
  simulateRule: DataProvider['simulateRule'] = (ruleId, params, days) =>
    this.rpc('simulateRule', [ruleId, params, days]);
  // ADR-002: o core do worker implementa os três; sem o repasse os hooks caíam no contorno
  // (contar objeções no cliente, resync geral, uma chamada por pedido).
  getPautaObjections: NonNullable<DataProvider['getPautaObjections']> = (q) =>
    this.rpc('getPautaObjections', [q]);
  syncSource: NonNullable<DataProvider['syncSource']> = (source) => this.rpc('syncSource', [source]);
  attributeManuallyBatch: NonNullable<DataProvider['attributeManuallyBatch']> = (orderIds, target) =>
    this.rpc('attributeManuallyBatch', [orderIds, target]);

  // V2 (SPEC-v2 6.3): métodos do Painel V2 implementados no worker (V2-WP2). Com eles aqui, o
  // `withPainelV2` do provider.ts deixa de completar com a fixture escrita à mão.
  getEconomics: NonNullable<PainelProviderV2['getEconomics']> = (q) => this.rpc('getEconomics', [q]);
  getKpisV2: NonNullable<PainelProviderV2['getKpisV2']> = (q) => this.rpc('getKpisV2', [q]);
  getChannels: NonNullable<PainelProviderV2['getChannels']> = (q) => this.rpc('getChannels', [q]);
  getBackend: NonNullable<PainelProviderV2['getBackend']> = (q) => this.rpc('getBackend', [q]);
  backend: NonNullable<PainelProviderV2['backend']> = (cmd) => this.rpc('backend', [cmd]);
  getLtv: NonNullable<PainelProviderV2['getLtv']> = (q) => this.rpc('getLtv', [q]);
  getTestLane: NonNullable<PainelProviderV2['getTestLane']> = (q) => this.rpc('getTestLane', [q]);
  getSlices: NonNullable<PainelProviderV2['getSlices']> = (q) => this.rpc('getSlices', [q]);
  getFunnelV2: NonNullable<PainelProviderV2['getFunnelV2']> = (q) => this.rpc('getFunnelV2', [q]);
  getPlan: NonNullable<PainelProviderV2['getPlan']> = () => this.rpc('getPlan', []);
  plan: NonNullable<PainelProviderV2['plan']> = (cmd) => this.rpc('plan', [cmd]);
  listTrackedLinks: NonNullable<PainelProviderV2['listTrackedLinks']> = () =>
    this.rpc('listTrackedLinks', []);
  upsertTrackedLink: NonNullable<PainelProviderV2['upsertTrackedLink']> = (l) =>
    this.rpc('upsertTrackedLink', [l]);
  updateEconomics: NonNullable<PainelProviderV2['updateEconomics']> = (patch) =>
    this.rpc('updateEconomics', [patch]);

  /**
   * Avisa o worker que a primeira tela pintou: começa a 2ª fase do boot (motor de regras,
   * decisões e briefings; 10.4). Idempotente.
   */
  warm(): void {
    if (this.warmed) return;
    this.warmed = true;
    this.post({ type: 'warm' });
  }

  subscribe(topics: Topic[], onMessage: (m: LiveMessage) => void, opts?: { lastSeq?: number }): Unsubscribe {
    const subId = ++this.subSeq;
    this.subs.set(subId, onMessage);
    this.post({
      type: 'subscribe',
      subId,
      topics,
      ...(opts?.lastSeq !== undefined ? { lastSeq: opts.lastSeq } : {}),
    });
    return () => {
      this.subs.delete(subId);
      this.post({ type: 'unsubscribe', subId });
    };
  }

  /** Encerra o worker (troca de provider, testes). */
  terminate(): void {
    this.failAll('Simulação encerrada');
    this.worker.terminate();
  }
}

function toCoreOptions(o: SimUrlOptions): MockCoreOptions {
  const out: MockCoreOptions = {};
  if (o.scenario && isScenario(o.scenario)) out.scenario = o.scenario as ScenarioId;
  else if (isScenarioV2(o.scenario)) out.scenarioV2 = o.scenario;
  if (o.seed) out.seed = o.seed;
  if (o.fixedAt) out.fixedAt = o.fixedAt;
  if (o.speed === 1 || o.speed === 10 || o.speed === 60) out.speed = o.speed;
  if (o.dateKey) out.dateKey = o.dateKey;
  return out;
}

/**
 * Worker aberto pelo `public/boot.js` antes do bundle do app (10.4), com o `init` já enviado.
 * O boot.js guarda aqui a 1ª resposta do handshake, se ela chegar antes de o app assumir.
 */
export interface EarlySim {
  worker: WorkerPort;
  /** Opções lidas da URL pelo boot.js (mesmo formato de `simOptionsFromUrl`). */
  options: SimUrlOptions;
  settled: FromWorker | null;
  failed: string | null;
}

declare global {
  interface Window {
    __rotaSim?: EarlySim;
  }
}

const sameOptions = (a: MockCoreOptions, b: MockCoreOptions): boolean =>
  a.scenario === b.scenario &&
  a.scenarioV2 === b.scenarioV2 &&
  a.seed === b.seed &&
  a.fixedAt === b.fixedAt &&
  a.speed === b.speed &&
  a.dateKey === b.dateKey;

/** Assume o worker do boot.js (uma vez só) quando ele subiu com as mesmas opções. */
function takeEarlySim(options: SimUrlOptions): EarlySim | null {
  if (typeof window === 'undefined') return null;
  const early = window.__rotaSim;
  if (!early) return null;
  window.__rotaSim = undefined;
  if (!sameOptions(toCoreOptions(early.options), toCoreOptions(options))) {
    early.worker.terminate();
    return null;
  }
  return early;
}

/**
 * Sobe o worker e espera o `ready` (reconstrução do dia; meta < 400 ms no M2, 8.1).
 * No build de produção o worker já subiu no boot.js, em paralelo com o bundle: só assume.
 * Rejeita se o `@rota/sim` não estiver disponível — o provider.ts cai no fixture.
 */
export function createMockProvider(options: SimUrlOptions = {}): Promise<MockProvider> {
  if (typeof Worker === 'undefined') return Promise.reject(new Error('Web Worker indisponível'));
  const early = takeEarlySim(options);
  const connected = early
    ? connectMockProvider(early.worker, options, HANDSHAKE_TIMEOUT_MS, early)
    : connectMockProvider(
        new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'rota-sim' }),
        options,
      );
  // 2ª fase do boot depois da 1ª pintura: o motor de regras (≈ 40 KB gz) não disputa a rede nem a
  // CPU com o caminho até o LCP. As decisões chegam logo em seguida, ao vivo.
  return connected.then((provider) => {
    whenPainted(() => provider.warm(), WARM_FALLBACK_MS);
    return provider;
  });
}

/**
 * Handshake `init` → `ready` sobre um worker já criado (exposto para os testes de conformidade).
 * Com `early` (worker do boot.js), o `init` já foi enviado e a resposta pode já ter chegado.
 */
export function connectMockProvider(
  worker: WorkerPort,
  options: SimUrlOptions = {},
  timeoutMs: number = HANDSHAKE_TIMEOUT_MS,
  early?: Pick<EarlySim, 'settled' | 'failed'>,
): Promise<MockProvider> {
  return new Promise<MockProvider>((resolve, reject) => {
    const settle = (m: FromWorker): boolean => {
      if (m.type === 'ready') {
        cleanup();
        resolve(new MockProvider(worker));
        return true;
      }
      if (m.type === 'unavailable') {
        cleanup();
        worker.terminate();
        reject(new Error(m.reason));
        return true;
      }
      return false;
    };
    const fail = (message: string): void => {
      cleanup();
      worker.terminate();
      reject(new Error(message));
    };
    const timer = setTimeout(() => fail('A simulação não respondeu a tempo'), timeoutMs);
    const onMessage = (e: MessageEvent<FromWorker>): void => {
      settle(e.data);
    };
    const onError = (e: ErrorEvent): void => fail(e.message || 'Falha ao carregar o worker da simulação');
    const cleanup = (): void => {
      clearTimeout(timer);
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
    };
    if (early?.failed) return fail(early.failed);
    if (early?.settled && settle(early.settled)) return;
    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    if (!early) worker.postMessage({ type: 'init', options: toCoreOptions(options) } satisfies ToWorker);
  });
}
